import {
    deleteFilterSession,
    getMasterFilterSession,
    getMasterFilterSessionDates,
    getMasterFilterSessionValues,
    getMasterFilterType,
    patchMasterFilterSession,
    postMasterFilterSelections,
    postMasterFilterSession,
} from '../../modules/client';
import { cloneDeep } from 'lodash';
import MasterFilterDateSelect from '../molecules/MasterFilterDateSelect';
import MasterFilterSelectGrid from '../molecules/MasterFilterSelectGrid';
import React, { Component } from 'react';
import {
    Button,
    Checkbox,
    Dropdown,
    Header,
    Input,
    Label,
    Modal,
    Popup,
    Segment,
} from 'semantic-ui-react';
import { toDateWithOptions } from 'date-fns/fp';

export default class MasterFilterModal extends Component<
    {
        clientId: Guid,
        onClose: () => void,
        onRefreshFilterSessions: () => void,
        onSave: () => void,
        open: boolean,
        key: string,
        filterSessions: any[],
    },
    {
        filterTemplateName: string,
        selectedFilter: string,
        selectedSession: ?Guid,
        loadedSession: ?Guid,
        filterTypes: any[],
        visibleFilterComponents: any[],
        selectedItems: any,
        from: Date,
        to: Date,
        newFilterMode: Boolean,
        showErrorModal: Boolean,
        errorText: string,
        disableButtons: Boolean,
    }
> {
    constructor() {
        super();
        this.state = {
            filterTemplateName: '',
            selectedFilter: null,
            selectedSession: null,
            loadedSession: null,
            filterTypes: [],
            visibleFilterComponents: [],
            selectedItems: new Map(),
            from: new Date(),
            to: new Date(),
            newFilterMode: false,
            showErrorModal: false,
            errorText: '',
            disableButtons: false,
        };
        this.resetStateAndClose = this.resetStateAndClose.bind(this);
    }
    uppercaseFirstLetter = (text: string) => {
        return text[0].toUpperCase() + text.substring(1);
    };
    componentDidMount(): void {
        getMasterFilterType(this.props.clientId).then((types) =>
            this.setState({ filterTypes: types })
        );
    }
    deleteFilter = () => {
        this.setState({ disableButtons: true });
        deleteFilterSession(this.state.loadedSession, this.props.clientId).then(() => {
            this.props.onRefreshFilterSessions();
            this.resetCurrentFilterState();
        });
    };
    filterTypeChanged = (event: any, data: any) => {
        this.setState({ selectedFilter: data.value });
    };
    resetCurrentFilterState = () => {
        this.setState({
            filterTemplateName: '',
            selectedFilter: null,
            visibleFilterComponents: [],
            selectedItems: new Map(),
            from: new Date(),
            to: new Date(),
            loadedSession: null,
            newFilterMode: false,
            showErrorModal: false,
            errorText: '',
            disableButtons: false,
        });
    };
    addFilter = (filterType) => {
        const displayName = this.uppercaseFirstLetter(filterType);
        let componentToAdd = undefined;
        switch (filterType) {
            case 'date':
                componentToAdd = (
                    <MasterFilterDateSelect
                        clientId={this.props.clientId}
                        filterType="date"
                        displayName="Date"
                        onRemove={this.removeFilter}
                        onSetDateRange={this.handleSetDateRange}
                        key="date"
                        fromInitialValue={this.state.from}
                        toInitialValue={this.state.to}
                    />
                );
                break;
            default:
                componentToAdd = (
                    <MasterFilterSelectGrid
                        clientId={this.props.clientId}
                        filterType={filterType}
                        displayName={displayName}
                        onRemove={this.removeFilter}
                        key={filterType}
                        onSelectListItem={this.handleSelectListItem}
                        initialSelectedValues={this.state.selectedItems.get(filterType)}
                    />
                );
        }
        this.setState({
            visibleFilterComponents: [...this.state.visibleFilterComponents, componentToAdd],
        });
    };
    removeFilter = (filterType) => {
        const filterListExcludingType = this.state.visibleFilterComponents.filter(
            (item) => item.props.filterType !== filterType
        );
        let newSelectedItems = cloneDeep(this.state.selectedItems);
        newSelectedItems.delete(filterType);
        this.setState({
            visibleFilterComponents: filterListExcludingType,
            selectedItems: newSelectedItems,
            selectedFilter: null,
        });
    };
    saveMasterFilterSession = () => {
        this.setState({ disableButtons: true });
        const saveSessionPromise =
            this.state.newFilterMode || this.state.loadedSession === null
                ? postMasterFilterSession(
                      this.props.clientId,
                      true,
                      this.state.filterTemplateName || ''
                  )
                : patchMasterFilterSession(
                      this.props.clientId,
                      true,
                      this.state.filterTemplateName || '',
                      this.state.loadedSession
                  );
        saveSessionPromise
            .then((session) => {
                let selectedFilters = [];
                this.state.selectedItems.forEach((value, key) => {
                    selectedFilters.push({ filterType: key, values: value.map((el) => el.key) });
                });
                const dateFilterUsed = this.state.visibleFilterComponents
                    .map((el) => el.props.filterType)
                    .includes('date');
                const sessionUid =
                    this.state.newFilterMode || this.state.loadedSession === null
                        ? session.SessionUID
                        : this.state.loadedSession;
                postMasterFilterSelections(
                    this.props.clientId,
                    sessionUid,
                    selectedFilters,
                    dateFilterUsed ? this.state.from : undefined,
                    dateFilterUsed ? this.state.to : undefined
                ).then(() => {
                    this.props.onRefreshFilterSessions();
                    this.resetStateAndClose();
                });
            })
            .catch((err) => {
                console.log(err);
                this.setState({
                    showErrorModal: true,
                    errorText: err.message,
                    disableButtons: false,
                });
            });
    };
    resetStateAndClose() {
        this.resetCurrentFilterState();
        this.setState({ selectedSession: null, newFilterMode: true });
        this.props.onClose();
    }
    handleSetDateRange = (filterType, from, to) => {
        if (from === undefined || to === undefined) {
            this.setState({ from: undefined, to: undefined });
            return;
        }
        this.setState({ from: from, to: to });
    };
    handleChangeTemplateName = (e, data) => {
        const newValue = data === undefined ? null : data.value;
        this.setState({ filterTemplateName: newValue });
    };
    handleSelectListItem = (filterType, component) => {
        const selectedRows = component.getSelectedRowsData();
        let newSelectedItems = cloneDeep(this.state.selectedItems);
        newSelectedItems.delete(filterType);
        newSelectedItems.set(filterType, selectedRows);
        this.setState({ selectedItems: newSelectedItems });
    };
    handleLoadFilterSession = () => {
        this.resetCurrentFilterState();
        this.setState({ disableButtons: true });
        const { TemplateName: templateName } = this.props.filterSessions.find(
            (el) => el.SessionUid === this.state.selectedSession
        );
        Promise.all([
            getMasterFilterSessionValues(this.props.clientId, this.state.selectedSession),
            getMasterFilterSessionDates(this.props.clientId, this.state.selectedSession),
        ]).then(([sessionValues, sessionDates]) => {
            if (sessionDates.length > 0) {
                this.setState({ from: sessionDates[0].BeginDate, to: sessionDates[0].EndDate });
                this.addFilter('date');
            }
            const distinctFilterTypes = [...new Set(sessionValues.map((el) => el.FilterType))];
            let newSelectedItems = new Map();
            distinctFilterTypes.map((filterType) => {
                newSelectedItems.set(
                    filterType,
                    sessionValues.filter((el) => el.FilterType === filterType).map((el) => el.Value)
                );
            });
            this.setState({
                loadedSession: this.state.selectedSession,
                filterTemplateName: templateName,
                selectedItems: newSelectedItems,
                disableButtons: false,
            });
            distinctFilterTypes.forEach((filterType) => this.addFilter(filterType));
        });
    };
    render() {
        const filterTypes = this.state.filterTypes
            .map((t) => ({
                text: this.uppercaseFirstLetter(t.FilterType),
                value: t.FilterType,
            }))
            .concat({ text: 'Date', value: 'date' });
        const usedFilterTypes = this.state.visibleFilterComponents.map(
            (item) => item.props.filterType
        );
        const unusedFilterTypes = filterTypes
            .filter((item) => !usedFilterTypes.includes(item.value))
            .sort((a, b) => (a.text > b.text ? 1 : -1));
        const filterSessionList = this.props.filterSessions.map((el) => ({
            text: el.TemplateName,
            value: el.SessionUid,
        }));
        return (
            <Modal open={this.props.open} size="small">
                <Header>
                    <i className="circular inverted blue filter icon"></i>
                    Global Filters
                </Header>
                <Modal.Content scrolling style={{ minHeight: '400px' }}>
                    <Segment.Group raised>
                        <Segment>
                            <div>
                                <Label as="a" color="blue" ribbon>
                                    Global Filter Name
                                </Label>
                            </div>
                            <div className="ui focus input">
                                <Input
                                    type="text"
                                    placeholder="Filter name..."
                                    maxLength="50"
                                    value={this.state.filterTemplateName}
                                    onChange={(e, data) => this.handleChangeTemplateName(e, data)}
                                />
                            </div>
                            <div className="ui focus input">
                                <Dropdown
                                    placeholder="Saved filter..."
                                    options={filterSessionList}
                                    selection
                                    onChange={(e, data) =>
                                        this.setState({ selectedSession: data.value })
                                    }
                                    value={this.state.selectedSession}
                                />
                                <Popup
                                    content="Load a saved filter"
                                    basic
                                    trigger={
                                        <Button
                                            className="ui circular icon button"
                                            onClick={this.handleLoadFilterSession}
                                            disabled={this.state.disableButtons}
                                        >
                                            <i aria-hidden="true" className="disk icon"></i>
                                        </Button>
                                    }
                                />
                                {this.state.disableButtons ? (
                                    <i className="large spinner loading icon"></i>
                                ) : null}
                            </div>
                            {this.state.loadedSession !== null ? (
                                <div>
                                    <Checkbox
                                        style={{ marginBottom: '1rem' }}
                                        checked={this.state.newFilterMode}
                                        onChange={() =>
                                            this.setState({
                                                newFilterMode: !this.state.newFilterMode,
                                            })
                                        }
                                        label="Save as new filter?"
                                    />
                                </div>
                            ) : null}
                        </Segment>
                        <Segment>
                            <div style={{ marginTop: '25px' }}>
                                <div>
                                    <Label as="a" color="blue" ribbon>
                                        Add Filter Types
                                    </Label>
                                </div>
                                <Dropdown
                                    placeholder="Select type..."
                                    options={unusedFilterTypes}
                                    selection
                                    value={this.state.selectedFilter}
                                    onChange={this.filterTypeChanged}
                                />
                                <Popup
                                    content="Add this type to your selected filters"
                                    basic
                                    trigger={
                                        <Button
                                            className="ui circular icon button"
                                            onClick={() =>
                                                this.addFilter(this.state.selectedFilter)
                                            }
                                            disabled={this.state.disableButtons}
                                        >
                                            <i aria-hidden="true" className="plus icon"></i>
                                        </Button>
                                    }
                                />
                            </div>
                        </Segment>
                        {this.state.visibleFilterComponents.length > 0 ? (
                            <Segment>
                                <div style={{ marginTop: '25px' }}>
                                    <Label as="a" color="blue" ribbon>
                                        Select Filter Options
                                    </Label>
                                    {this.state.visibleFilterComponents}
                                </div>
                            </Segment>
                        ) : null}
                    </Segment.Group>
                </Modal.Content>
                <Modal.Actions>
                    {this.state.loadedSession !== null ? (
                        <Button
                            content="Delete Filter"
                            disabled={this.state.disableButtons}
                            style={{ float: 'left' }}
                            onClick={this.deleteFilter}
                            icon="delete"
                        />
                    ) : null}
                    {this.state.disableButtons ? (
                        <i className="large spinner loading icon"></i>
                    ) : null}
                    <Button
                        content="Reset Filter"
                        disabled={this.state.disableButtons}
                        onClick={() => {
                            this.resetCurrentFilterState();
                            this.setState({ selectedSession: null });
                        }}
                        icon="undo"
                    />
                    <Button
                        content="Save Filter"
                        disabled={
                            this.state.disableButtons ||
                            this.state.filterTemplateName.length === 0 ||
                            this.state.visibleFilterComponents.length === 0
                        }
                        onClick={this.saveMasterFilterSession}
                        primary
                        icon="save"
                    />
                    <Button
                        content="Close"
                        disabled={this.state.disableButtons}
                        onClick={this.resetStateAndClose}
                        icon="window close"
                    />
                </Modal.Actions>

                <Modal open={this.state.showErrorModal} size="small">
                    <Modal.Content>
                        <h3>{this.state.errorText}</h3>
                    </Modal.Content>
                    <Modal.Actions>
                        <Button
                            icon="check"
                            content="Ok"
                            onClick={() => this.setState({ showErrorModal: false, errorText: '' })}
                        />
                    </Modal.Actions>
                </Modal>
            </Modal>
        );
    }
}



// WEBPACK FOOTER //
// ./components/molecules/MasterFilterModal.jsx